import axios from "axios";

// ---------------------------------------------------------------------------
// Offline mutation queue (IndexedDB-backed).
//
// When a data-entry request (POST/PUT/DELETE on whitelisted endpoints) fails
// because the device is offline / the server is unreachable, the request is
// stored locally and automatically replayed once the connection is back.
// The UI receives an optimistic ("queued") response so nothing is lost.
// ---------------------------------------------------------------------------

const DB_NAME = "rnetwork-offline";
const STORE = "mutations";
const DB_VERSION = 1;

let dbPromise = null;

function getDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbAll() {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(item) {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).put(item);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(key) {
  const db = await getDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const req = tx.objectStore(STORE).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// --- Configuration: which requests may be queued ---------------------------

const ALLOWED_PREFIXES = [
  "/api/customers",
  "/api/serial-numbers",
  "/api/service-reports",
  "/api/tasks",
  "/api/chat/messages",
  "/api/knowledge/articles",
];

// Never queue these (need a live connection or carry binary/multipart data).
const BLOCKED_SUBSTRINGS = ["/export", "/import", "/documents", "/attachments", "/pdf", "/download"];

function entityLabel(url = "") {
  if (url.includes("/service-reports")) return "Servicebericht";
  if (url.includes("/serial-numbers")) return "Seriennummer";
  if (url.includes("/customers")) return "Kunde";
  if (url.includes("/tasks")) return "Aufgabe";
  if (url.includes("/chat/messages")) return "Nachricht";
  if (url.includes("/knowledge/articles")) return "Artikel";
  return "Eintrag";
}

// Resolve the full request path including a possible axios baseURL (e.g. the
// shared `api` instance uses baseURL "/api" and url "/tasks").
function fullPath(config = {}) {
  const url = config.url || "";
  if (/^https?:\/\//i.test(url)) return url; // already absolute
  const base = (config.baseURL || "").replace(/\/+$/, "");
  if (base && !url.startsWith(base)) {
    return base + (url.startsWith("/") ? url : "/" + url);
  }
  return url;
}

export function isQueueable(config) {
  if (!config) return false;
  const method = (config.method || "get").toLowerCase();
  if (!["post", "put", "delete", "patch"].includes(method)) return false;
  const url = fullPath(config);
  if (!ALLOWED_PREFIXES.some((p) => url.startsWith(p))) return false;
  if (BLOCKED_SUBSTRINGS.some((s) => url.includes(s))) return false;
  if (typeof FormData !== "undefined" && config.data instanceof FormData) return false;
  if (config.responseType === "blob" || config.responseType === "arraybuffer") return false;
  return true;
}

function lastSegment(url = "") {
  const path = url.split("?")[0];
  const parts = path.split("/").filter(Boolean);
  return parts[parts.length - 1];
}

function genTempId() {
  return "offline-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
}

function optimisticResponse(method, body, id) {
  const data =
    method === "delete" ? { success: true, _offline: true } : { ...(body || {}), id, _offline: true };
  return { data, status: 202, statusText: "Queued (offline)", headers: {}, config: {}, _queued: true };
}

// --- Change notification ----------------------------------------------------

let changeHandler = () => {};
export function setQueueChangeHandler(fn) {
  changeHandler = typeof fn === "function" ? fn : () => {};
}

export async function getPendingCount() {
  try {
    const items = await idbAll();
    return items.length;
  } catch {
    return 0;
  }
}

// --- Enqueue ----------------------------------------------------------------

async function enqueueMutation(config) {
  const method = (config.method || "post").toLowerCase();
  const url = fullPath(config);
  let body = config.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      /* keep raw string */
    }
  }

  const items = await idbAll();

  if (method !== "post") {
    const targetId = lastSegment(url);

    // Edit/delete of a record that was itself created offline (still pending).
    const pendingCreate = items.find((it) => it.method === "post" && it.tempId === targetId);
    if (pendingCreate) {
      if (method === "delete") {
        await idbDelete(pendingCreate.key);
      } else {
        pendingCreate.body = { ...(pendingCreate.body || {}), ...(body || {}) };
        await idbPut(pendingCreate);
      }
      changeHandler();
      return optimisticResponse(method, body, targetId);
    }

    // Collapse repeated edits to the same record into one queued PUT.
    if (method === "put" || method === "patch") {
      const pendingPut = items.find(
        (it) => (it.method === "put" || it.method === "patch") && lastSegment(it.url) === targetId
      );
      if (pendingPut) {
        pendingPut.body = { ...(pendingPut.body || {}), ...(body || {}) };
        await idbPut(pendingPut);
        changeHandler();
        return optimisticResponse(method, body, targetId);
      }
    }
  }

  const tempId = method === "post" ? genTempId() : lastSegment(url);
  await idbPut({
    method,
    url,
    body,
    tempId: method === "post" ? tempId : null,
    label: entityLabel(url),
    createdAt: new Date().toISOString(),
  });
  changeHandler();
  return optimisticResponse(method, body, tempId);
}

// --- Flush (replay) ---------------------------------------------------------

// Dedicated instance so the replay requests are NOT intercepted/re-queued.
const replayClient = axios.create({ withCredentials: true });

let flushing = false;

export async function flushQueue() {
  if (flushing) return { synced: 0, failed: 0, remaining: await getPendingCount() };
  flushing = true;
  let synced = 0;
  let failed = 0;
  try {
    const items = (await idbAll()).sort((a, b) => a.key - b.key);
    for (const item of items) {
      try {
        await replayClient.request({ method: item.method, url: item.url, data: item.body });
        await idbDelete(item.key);
        synced += 1;
        changeHandler();
      } catch (err) {
        if (!err.response) {
          // Still offline / unreachable -> keep remaining items, retry later.
          break;
        }
        // Server rejected the request (4xx/5xx) -> drop it to avoid an endless loop.
        await idbDelete(item.key);
        failed += 1;
        changeHandler();
      }
    }
  } finally {
    flushing = false;
  }
  return { synced, failed, remaining: await getPendingCount() };
}

// --- Interceptor ------------------------------------------------------------

const attached = new WeakSet();

function attachInterceptor(instance) {
  if (!instance || attached.has(instance)) return;
  attached.add(instance);
  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error.config;
      const isNetworkError = !error.response; // no HTTP response => offline/unreachable/timeout
      if (isNetworkError && isQueueable(config)) {
        try {
          const res = await enqueueMutation(config);
          window.dispatchEvent(
            new CustomEvent("offline-queued", { detail: { label: entityLabel(fullPath(config)) } })
          );
          return res;
        } catch {
          /* fall through to reject */
        }
      }
      return Promise.reject(error);
    }
  );
}

// Attach to the global axios default instance.
export function installOfflineInterceptor() {
  attachInterceptor(axios);
}

// Attach to a custom axios instance (e.g. the shared `api` instance).
export function attachOfflineInterceptor(instance) {
  attachInterceptor(instance);
}
